import { SettingsFormSchema, SchemaField } from "@kalyzee/kast-app-web-components";

export const SETTINGS_SCHEMA_VERSION = 6;

// ---- Reusable sub-schemas ----

const audioVideoBitrateSchema = (label: string): SchemaField => ({
  type: 'object',
  label,
  properties: {
    audio: { type: 'number', label: 'Audio (bps)', min: 0, step: 1000 },
    video: { type: 'number', label: 'Vidéo (bps)', min: 0, step: 1000 },
  },
});

// ---- PTZ sub-schema (MeetingSourceDevicePTZ) ----

const ptzSchema: SchemaField = {
  type: 'object',
  label: 'PTZ',
  properties: {
    mode: {
      type: 'select',
      label: 'Mode',
      required: true,
      options: [
        { label: 'Aver', value: 'aver' },
        { label: 'Custom', value: 'custom' },
      ],
    },
  },
  discriminator: {
    field: 'mode',
    mapping: {
      aver: {
        data: {
          type: 'object',
          label: 'Configuration Aver',
          required: true,
          properties: {
            hostname: { type: 'string', label: 'Hostname', required: true, placeholder: '192.168.1.x' },
            username: { type: 'string', label: 'Nom d\'utilisateur' },
            password: { type: 'string', label: 'Mot de passe' },
          },
        },
      },
      custom: {
        custom: { type: 'string', label: 'Custom PTZ (JSON)', description: 'Objet { move, stopMove, zoom, stopZoom, loadPreset, setPreset, scene }', multiline: true },
      },
    },
  },
};

// ---- MediaDeviceInfoSelector sub-schema ----
// { kind?, deviceId?, groupId?, label?, index? } — values can be string | { regex, flag? }

const mediaDeviceSelectorSchema = (label: string): SchemaField => ({
  type: 'string',
  label,
  description: 'String (deviceId) ou JSON : { "label": { "regex": "cam", "flag": "i" } }',
  multiline: true,
  placeholder: '{ "label": { "regex": "cam" } }',
});

// ---- Device data per type (discriminated by device.type) ----

const ovenMediaDataProperties: Record<string, SchemaField> = {
  url: { type: 'string', label: 'URL', required: true, placeholder: 'wss://...' },
  sdpFormatter: { type: 'string', label: 'SDP Formatter' },
  ptz: ptzSchema,
  byPassAudioEncoder: { type: 'boolean', label: 'Bypass audio encoder' },
  byPassVideoEncoder: { type: 'boolean', label: 'Bypass video encoder' },
};

const kastDataProperties: Record<string, SchemaField> = {
  ip: { type: 'string', label: 'IP', required: true, placeholder: '192.168.1.x' },
  port: { type: 'number', label: 'Port', step: 1 },
  defaultScene: { type: 'string', label: 'Scène par défaut (JSON)', multiline: true },
  defaultView: { type: 'string', label: 'Vue par défaut (JSON)', multiline: true },
  mediaConstraints: { type: 'string', label: 'Media Constraints (JSON)', multiline: true, placeholder: '{ "video": true, "audio": true }' },
};

const whiteboardDataProperties: Record<string, SchemaField> = {
  ip: { type: 'string', label: 'IP', required: true, placeholder: '192.168.1.x' },
  port: { type: 'number', label: 'Port', step: 1 },
  defaultVideoSource: { type: 'string', label: 'Source vidéo par défaut', placeholder: 'screen' },
  defaultAudioSource: {
    type: 'select', label: 'Source audio par défaut',
    options: [
      { label: 'Playback', value: 'playback' },
      { label: 'Microphone', value: 'microphone' },
      { label: 'Mixed', value: 'mixed' },
    ],
  },
  defaultPreferVideoSource: { type: 'string', label: 'Source vidéo préférée', placeholder: 'screen' },
  defaultPreferAudioSource: {
    type: 'select', label: 'Source audio préférée',
    options: [
      { label: 'Playback', value: 'playback' },
      { label: 'Microphone', value: 'microphone' },
      { label: 'Mixed', value: 'mixed' },
    ],
  },
  defaultAudioPlaybackVolume: { type: 'number', label: 'Volume playback', min: 0, max: 1, step: 0.1 },
  defaultAudioMicrophoneVolume: { type: 'number', label: 'Volume micro', min: 0, max: 1, step: 0.1 },
  mediaConstraints: { type: 'string', label: 'Media Constraints (JSON)', multiline: true, placeholder: '{ "video": true, "audio": true }' },
};

const webDataProperties: Record<string, SchemaField> = {
  disableShareScreen: { type: 'boolean', label: 'Désactiver partage d\'écran' },
  defaultVideoDevice: mediaDeviceSelectorSchema('Device vidéo par défaut'),
  defaultAudioDevice: mediaDeviceSelectorSchema('Device audio par défaut'),
  filterVideoDevices: mediaDeviceSelectorSchema('Filtre devices vidéo'),
  filterAudioDevices: mediaDeviceSelectorSchema('Filtre devices audio'),
  excludeVideoDevices: mediaDeviceSelectorSchema('Exclure devices vidéo'),
  excludeAudioDevices: mediaDeviceSelectorSchema('Exclure devices audio'),
  hideVideoDevices: { type: 'boolean', label: 'Masquer devices vidéo' },
  hideAudioDevices: { type: 'boolean', label: 'Masquer devices audio' },
  hideVideoDevicesIfNoSeveralDevices: { type: 'boolean', label: 'Masquer vidéo si 1 seul device' },
  hideAudioDevicesIfNoSeveralDevices: { type: 'boolean', label: 'Masquer audio si 1 seul device' },
  autoSetVideoDevices: { type: 'string', label: 'Auto-set vidéo (JSON)', description: 'Array de MediaDeviceInfoSelector + priority', multiline: true },
  autoSetAudioDevices: { type: 'string', label: 'Auto-set audio (JSON)', description: 'Array de MediaDeviceInfoSelector + priority', multiline: true },
  mediaConstraints: { type: 'string', label: 'Media Constraints (JSON)', multiline: true, placeholder: '{ "video": true, "audio": true }' },
  children: { type: 'string', label: 'Children devices (JSON)', description: 'Array de MeetingSourceDeviceWebChild', multiline: true },
  ptz: ptzSchema,
  keepAlwaysAudioTrackAlive: { type: 'boolean', label: 'Garder la piste audio toujours active' },
  keepAlwaysVideoTrackAlive: { type: 'boolean', label: 'Garder la piste vidéo toujours active' },
};

const emptyDataProperties: Record<string, SchemaField> = {
  backgroundColor: { type: 'string', label: 'Couleur de fond', placeholder: '#000000' },
};

const customDataProperties: Record<string, SchemaField> = {
  // custom is `any` → free-form JSON
  _raw: { type: 'string', label: 'Configuration (JSON)', multiline: true, placeholder: '{ }' },
};

// ---- MeetingSourceDevice ----

const meetingSourceDeviceSchema: SchemaField = {
  type: 'object',
  label: 'Device',
  properties: {
    mode: {
      type: 'select',
      label: 'Mode',
      required: true,
      options: [
        { label: 'Audio + Vidéo', value: 'audiovideo' },
        { label: 'Audio', value: 'audio' },
        { label: 'Vidéo', value: 'video' },
      ],
    },
    type: {
      type: 'select',
      label: 'Type de device',
      required: true,
      options: [
        { label: 'OvenMedia', value: 'ovenmedia' },
        { label: 'Whiteboard', value: 'whiteboard' },
        { label: 'Kast', value: 'kast' },
        { label: 'Web (navigateur)', value: 'web' },
        { label: 'Vide', value: 'empty' },
        { label: 'Custom', value: 'custom' },
      ],
    },
    disabled: { type: 'boolean', label: 'Désactivé' },
    priority: { type: 'number', label: 'Priorité', step: 1 },
    unmuted: { type: 'boolean', label: 'Non muté par défaut' },
    extra: {
      type: 'object',
      label: 'Extra',
      properties: {
        style: { type: 'string', label: 'Style (JSON CSS)', multiline: true, placeholder: '{ }' },
      },
    },
  },
  discriminator: {
    field: 'type',
    mapping: {
      ovenmedia: { data: { type: 'object', label: 'Configuration OvenMedia', required: true, properties: ovenMediaDataProperties } },
      kast: { data: { type: 'object', label: 'Configuration Kast', required: true, properties: kastDataProperties } },
      whiteboard: { data: { type: 'object', label: 'Configuration Whiteboard', required: true, properties: whiteboardDataProperties } },
      web: { data: { type: 'object', label: 'Configuration Web', required: true, properties: webDataProperties } },
      empty: { data: { type: 'object', label: 'Configuration Empty', required: true, properties: emptyDataProperties } },
      custom: { data: { type: 'object', label: 'Configuration Custom', required: true, properties: customDataProperties } },
    },
  },
};

// ---- MeetingSourceExtended (Partial<>) ----

const meetingSourceSchema = (label: string): SchemaField => ({
  type: 'object',
  label,
  properties: {
    name: { type: 'string', label: 'Nom', required: true, placeholder: 'webcam, screen, ...' },
    extra: {
      type: 'object',
      label: 'Type de source',
      required: true,
      properties: {
        type: {
          type: 'select',
          label: 'Type',
          required: true,
          options: [
            { label: 'Caméra', value: 'camera' },
            { label: 'Écran', value: 'screen' },
          ],
        },
      },
    },
    devices: {
      type: 'array',
      label: 'Devices',
      required: true,
      items: meetingSourceDeviceSchema,
    },
    id: { type: 'string', label: 'ID' },
    disabled: { type: 'boolean', label: 'Désactivé' },
    hideIfNoMedia: { type: 'boolean', label: 'Masquer si pas de média' },
    priority: { type: 'number', label: 'Priorité', step: 1 },
    visible: { type: 'boolean', label: 'Visible' },
    controls: {
      type: 'object',
      label: 'Contrôles PTZ (source)',
      description: 'MeetingSourceControls',
      properties: {
        type: { type: 'string', label: 'Type', default: 'aver-ptz', required: true, placeholder: 'aver-ptz' },
        hostname: { type: 'string', label: 'Hostname', placeholder: '192.168.1.x' },
        username: { type: 'string', label: 'Nom d\'utilisateur' },
        password: { type: 'string', label: 'Mot de passe' },
        entryPreset: { type: 'number', label: 'Preset d\'entrée', step: 1 },
        exitPreset: { type: 'number', label: 'Preset de sortie', step: 1 },
      },
    },
  },
});

const secondaryScreenColumnSchema: SchemaField = {
  type: 'object',
  label: 'Colonne',
  properties: {
    contentType: {
      type: 'select',
      label: 'Type de contenu',
      required: true,
      options: [
        { label: 'Lecteurs vidéo', value: 'players' },
        { label: 'Chat', value: 'chat' },
        { label: 'Utilisateurs', value: 'users' },
      ],
    },
    videoContainerId: { type: 'string', label: 'ID conteneur vidéo', description: 'Requis si contentType = "players"' },
    playerRows: { type: 'number', label: 'Lignes', min: 1, step: 1 },
    playerColumns: { type: 'number', label: 'Colonnes', min: 1, step: 1 },
    playerBoxPreset: {
      type: 'select',
      label: 'Preset',
      options: [
        { label: 'Par défaut', value: 'default' },
        { label: 'Audio', value: 'audio' },
        { label: 'Grille seule', value: 'gridonly' },
      ],
    },
    style: {
      type: 'string',
      label: 'Style (JSON CSS)',
      description: 'Ex: { "width": "75%", "height": "100%" }',
      multiline: true,
      placeholder: '{ "width": "25%", "height": "100%" }',
    },
    playerStyle: {
      type: 'string',
      label: 'Style des players (JSON CSS)',
      multiline: true,
      placeholder: '{ "borderRadius": 0 }',
    },
    overlayMode: {
      type: 'select',
      label: 'Mode overlay des players',
      description: 'Contrôle l\'affichage du header des players',
      options: [
        { label: 'Overlay (par défaut)', value: 'overlay' },
        { label: 'Overlay au survol', value: 'overlay-hoverable' },
        { label: 'Normal (au-dessus)', value: 'normal' },
      ],
    },
    singlePlayerMode: {
      type: 'boolean',
      label: 'Mode player unique',
      description: 'Force 1 colonne / 1 ligne et affiche l\'indicateur de page en overlay',
    },
  },
};

const secondaryScreenVariantSchema: SchemaField = {
  type: 'object',
  label: 'Variante',
  properties: {
    match: { type: 'string', label: 'Condition (match)', description: 'Expression de correspondance appliquée au contexte (meeting, carouselIds)' },
    layoutStyle: {
      type: 'string',
      label: 'Style du layout (JSON CSS)',
      multiline: true,
      placeholder: '{ "padding": "2em" }',
    },
    justifyContent: {
      type: 'select',
      label: 'Justify Content',
      options: [
        { label: 'flex-start', value: 'flex-start' },
        { label: 'center', value: 'center' },
        { label: 'flex-end', value: 'flex-end' },
        { label: 'space-between', value: 'space-between' },
        { label: 'space-around', value: 'space-around' },
        { label: 'space-evenly', value: 'space-evenly' },
      ],
    },
    topBar: {
      type: 'object',
      label: 'Barre supérieure',
      properties: {
        brand: { type: 'boolean', label: 'Marque' },
        watchers: { type: 'boolean', label: 'Spectateurs' },
        user: { type: 'boolean', label: 'Utilisateur' },
        timer: { type: 'boolean', label: 'Timer' },
        meetingName: { type: 'boolean', label: 'Nom du meeting' },
      },
    },
    columns: {
      type: 'array',
      label: 'Colonnes',
      items: secondaryScreenColumnSchema,
    },
  },
};

const secondaryScreenLayoutSchema: SchemaField = {
  type: 'object',
  label: 'Layout',
  properties: {
    id: { type: 'number', label: 'ID', description: 'Identifiant du layout (vide = layout par défaut)', step: 1 },
    videoContainers: {
      type: 'array',
      label: 'Conteneurs vidéo',
      description: 'Conteneurs logiques liés à un carousel et un groupe',
      items: {
        type: 'object',
        label: 'Conteneur',
        properties: {
          id: { type: 'string', label: 'ID', required: true },
          carouselId: { type: 'string', label: 'ID Carousel' },
          groupId: { type: 'string', label: 'ID Groupe' },
          capacity: { type: 'number', label: 'Capacité', required: true, min: 1, step: 1 },
        },
      },
    },
    variants: {
      type: 'array',
      label: 'Variantes',
      items: secondaryScreenVariantSchema,
    },
  },
};

// ---- Full settings schema ----

export const settingsSchema: SettingsFormSchema = {
  properties: {
    secret: {
      type: 'object',
      label: 'Sécurité',
      description: 'Mots de passe et codes PIN',
      properties: {
        adminPin: { type: 'string', label: 'PIN administrateur', placeholder: 'Code PIN' },
        localPassword: { type: 'string', label: 'Mot de passe local', placeholder: 'Mot de passe' },
        remotePassword: { type: 'string', label: 'Mot de passe distant', placeholder: 'Mot de passe' },
      },
    },
    roomId: { type: 'string', label: 'ID de salle', description: 'Identifiant de la salle de réunion', placeholder: 'ex: room-123' },
    dev: { type: 'boolean', label: 'Mode développeur', description: 'Active le mode développeur' },
    logs: { type: 'boolean', label: 'Logs', description: 'Active les logs (simple). Pour une config avancée, utilisez l\'import JSON.' },
    soundIndicators: { type: 'boolean', label: 'Indicateurs sonores', description: 'Affiche les indicateurs de volume audio' },
    ui: {
      type: 'object',
      label: 'Interface',
      description: "Options d'affichage",
      properties: {
        hideLogoutButton: { type: 'boolean', label: 'Masquer le bouton déconnexion' },
      },
    },
    bridge: {
      type: 'object',
      label: 'Bridge (messagerie)',
      description: 'Configuration du pont de communication master/slave',
      properties: {
        master: {
          type: 'object',
          label: 'Master',
          properties: {
            enableBroadcast: { type: 'boolean', label: 'Activer le broadcast' },
          },
        },
        slave: {
          type: 'object',
          label: 'Slave',
          properties: {
            enableBroadcast: { type: 'boolean', label: 'Activer le broadcast' },
            enableWebsocket: { type: 'boolean', label: 'Activer le WebSocket' },
            websocketEndpoint: { type: 'string', label: 'Endpoint WebSocket', placeholder: 'wss://...' },
          },
        },
      },
    },
    sources: {
      type: 'array',
      label: 'Sources',
      description: 'Sources média (caméras, écrans, etc.)',
      items: meetingSourceSchema('Source'),
    },
    videoFeedback: {
      type: 'object',
      label: 'Retour vidéo',
      description: 'Source utilisée pour le retour vidéo',
      properties: {
        source: meetingSourceSchema('Source'),
      },
    },
    webcamFeedback: {
      type: 'object',
      label: 'Retour webcam',
      description: 'Source utilisée pour le retour webcam',
      properties: {
        source: meetingSourceSchema('Source'),
      },
    },
    debug: {
      type: 'object',
      label: 'Debug',
      description: 'Options de débogage',
      properties: {
        displaySessionIds: { type: 'boolean', label: 'Afficher les IDs de session' },
        displayIncomingStreamStats: { type: 'boolean', label: 'Stats flux entrants' },
        displayOutgoingStreamStats: { type: 'boolean', label: 'Stats flux sortants' },
        audio: {
          type: 'object',
          label: 'Audio',
          properties: {
            mixer: {
              type: 'object',
              label: 'Mixer',
              properties: {
                public: { type: 'boolean', label: 'Public' },
                useMediaStreamDestination: { type: 'boolean', label: 'Utiliser MediaStreamDestination' },
                disableAutoResumeAudioContext: { type: 'boolean', label: 'Désactiver auto-resume AudioContext' },
                equalizer: { type: 'boolean', label: 'Égaliseur' },
                notUseCompressor: { type: 'boolean', label: 'Désactiver le compresseur' },
              },
            },
          },
        },
        sdkClient: {
          type: 'object',
          label: 'SDK Client',
          properties: {
            public: { type: 'boolean', label: 'Public' },
          },
        },
        meetingContext: {
          type: 'object',
          label: 'Meeting Context',
          properties: {
            public: { type: 'boolean', label: 'Public' },
          },
        },
        maxBitrate: {
          type: 'object',
          label: 'Bitrate max',
          description: 'Limites de bitrate par rôle',
          properties: {
            pedagogy: {
              type: 'object',
              label: 'Pédagogie',
              properties: {
                webcam: audioVideoBitrateSchema('Webcam'),
                screensharing: audioVideoBitrateSchema('Partage d\'écran'),
              },
            },
            student: {
              type: 'object',
              label: 'Étudiant',
              properties: {
                webcam: audioVideoBitrateSchema('Webcam'),
                screensharing: audioVideoBitrateSchema('Partage d\'écran'),
              },
            },
            expert: {
              type: 'object',
              label: 'Expert',
              properties: {
                camera: audioVideoBitrateSchema('Caméra'),
                screen: audioVideoBitrateSchema('Écran'),
                webcam: audioVideoBitrateSchema('Webcam'),
              },
            },
          },
        },
      },
    },
    emission: {
      type: 'object',
      label: 'Émission',
      description: 'Configuration de l\'émission WebRTC',
      properties: {
        sdpFormatter: {
          type: 'object',
          label: 'SDP Formatter',
          properties: {
            offer: { type: 'string', label: 'Offer', multiline: true, placeholder: 'SDP formatter pour les offres' },
            answer: { type: 'string', label: 'Answer', multiline: true, placeholder: 'SDP formatter pour les réponses' },
          },
        },
      },
    },
    carousel: {
      type: 'object',
      label: 'Carousel',
      description: 'Configuration du carousel des écrans secondaires',
      properties: {
        prelaodDelay: { type: 'number', label: 'Délai de préchargement (ms)', min: 0, step: 100 },
        interval: { type: 'number', label: 'Intervalle (ms)', min: 0, step: 1000 },
        layouts: {
          type: 'array',
          label: 'Layouts',
          description: 'Dispositions des écrans secondaires',
          items: secondaryScreenLayoutSchema,
        },
        filters: {
          type: 'array',
          label: 'Filtres',
          description: 'Filtres par carousel',
          items: {
            type: 'object',
            properties: {
              carouselId: { type: 'string', label: 'ID Carousel', required: true },
              value: { type: 'string', label: 'Valeur du filtre', required: true },
            },
          },
        },
      },
    },
    features: {
      type: 'object',
      label: 'Fonctionnalités',
      description: 'Activation et configuration des fonctionnalités',
      properties: {
        survey: { type: 'boolean', label: 'Sondages' },
        board: { type: 'boolean', label: 'Montée au tableau' },
        boardMatch: { type: 'string', label: 'Filtre board (matchEngine)' },
        audioMatch: { type: 'string', label: 'Filtre audio (matchEngine)' },
      },
    },
  },
};

// ---- Helpers ----

/**
 * Merge base properties with discriminator-mapped properties based on the actual data.
 * Mirrors the logic in SettingsForm's getEffectiveProperties().
 */
const getEffectiveProperties = (field: SchemaField & { type: 'object' }, obj: any): Record<string, SchemaField> => {
  if (!field.discriminator || typeof obj !== 'object' || obj === null) return field.properties;
  const discVal = obj[field.discriminator.field];
  const extra = (discVal !== undefined && field.discriminator.mapping[String(discVal)]) || {};
  return { ...field.properties, ...extra };
};

// ---- Sanitization ----

const getSchemaDefault = (field: SchemaField): any => {
  switch (field.type) {
    case 'string': return field.default ?? '';
    case 'number': return field.default ?? 0;
    case 'boolean': return field.default ?? false;
    case 'select': return field.default ?? (field.options.length > 0 ? field.options[0].value : '');
    case 'object': return {};
    case 'array': return field.default ?? [];
  }
};

const getExpectedType = (field: SchemaField): string => {
  switch (field.type) {
    case 'string': return 'string';
    case 'number': return 'number';
    case 'boolean': return 'boolean';
    case 'select': return typeof (field.options[0]?.value ?? '');
    case 'object': return 'object';
    case 'array': return 'array';
  }
};

const matchesType = (value: any, field: SchemaField): boolean => {
  if (value === null || value === undefined) return true; // optional fields
  const expected = getExpectedType(field);
  if (expected === 'array') return Array.isArray(value);
  if (expected === 'object') return typeof value === 'object' && !Array.isArray(value);
  return typeof value === expected;
};

/**
 * Sanitize an imported settings object against the schema.
 * - Removes keys not in the schema
 * - Replaces values with wrong types by schema defaults
 * - Recursively walks objects and arrays
 * Returns { sanitized, warnings } where warnings list what was changed.
 */
export const sanitizeSettings = (
  data: any,
  schema: SettingsFormSchema,
): { sanitized: any; warnings: string[] } => {
  const warnings: string[] = [];

  const sanitizeObject = (obj: any, properties: Record<string, SchemaField>, path: string): any => {
    if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
      warnings.push(`${path || 'racine'}: attendu un objet, remplacé par {}`);
      return {};
    }
    const result: any = {};

    // Remove unknown keys (but keep _hidden_ prefixed keys)
    for (const key of Object.keys(obj)) {
      if (!(key in properties) && !key.startsWith('_hidden_')) {
        warnings.push(`${path ? path + '.' : ''}${key}: champ inconnu, supprimé`);
      }
    }

    // Validate known keys
    for (const [key, field] of Object.entries(properties)) {
      const fullPath = path ? `${path}.${key}` : key;
      if (!(key in obj) || obj[key] === undefined) continue; // optional, skip

      const value = obj[key];
      result[key] = sanitizeField(value, field, fullPath);
    }

    // Preserve _hidden_ prefixed keys as-is
    for (const key of Object.keys(obj)) {
      if (!key.startsWith('_hidden_')) continue;
      // _hidden_<field> is redundant when <field> already exists (but _hidden_items_<field> can coexist)
      if (key.startsWith('_hidden_items_')) {
        result[key] = obj[key];
      } else {
        const visibleKey = key.slice('_hidden_'.length);
        if (visibleKey in result) {
          warnings.push(`${path ? path + '.' : ''}${key}: ignoré car ${visibleKey} existe déjà`);
        } else {
          result[key] = obj[key];
        }
      }
    }

    return result;
  };

  const sanitizeField = (value: any, field: SchemaField, path: string): any => {
    if (value === null || value === undefined) return value;

    // Check for JSON string fields (data, extra, style in our schema are strings that hold JSON)
    if (field.type === 'string') {
      if (typeof value === 'object') {
        // An object where we expect a JSON string — convert it
        try {
          return JSON.stringify(value, null, 2);
        } catch {
          warnings.push(`${path}: objet non sérialisable, remplacé par la valeur par défaut`);
          return getSchemaDefault(field);
        }
      }
      if (typeof value !== 'string') {
        warnings.push(`${path}: attendu string, reçu ${typeof value}, remplacé par la valeur par défaut`);
        return getSchemaDefault(field);
      }
      return value;
    }

    if (!matchesType(value, field)) {
      warnings.push(`${path}: attendu ${getExpectedType(field)}, reçu ${Array.isArray(value) ? 'array' : typeof value}, remplacé par la valeur par défaut`);
      return getSchemaDefault(field);
    }

    if (field.type === 'object') {
      return sanitizeObject(value, getEffectiveProperties(field, value), path);
    }

    if (field.type === 'array') {
      if (!Array.isArray(value)) return getSchemaDefault(field);
      return value.map((item, i) => sanitizeField(item, field.items, `${path}[${i}]`));
    }

    return value;
  };

  const sanitized = sanitizeObject(data, schema.properties, '');
  return { sanitized, warnings };
};

// ---- Prepare for form (silent coercion, no warnings) ----

/**
 * Coerce settings values to match schema expectations before feeding to SettingsForm.
 * Mainly converts JS objects to JSON strings where the schema declares a string field.
 */
export const prepareSettingsForForm = (data: any, schema: SettingsFormSchema): any => {
  const coerceObject = (obj: any, properties: Record<string, SchemaField>): any => {
    if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) return {};
    const result: any = {};
    for (const [key, field] of Object.entries(properties)) {
      if (!(key in obj) || obj[key] === undefined) continue;
      result[key] = coerceField(obj[key], field);
    }
    // Preserve _hidden_ prefixed keys as-is
    for (const key of Object.keys(obj)) {
      if (!key.startsWith('_hidden_')) continue;
      if (key.startsWith('_hidden_items_')) {
        result[key] = obj[key];
      } else {
        const visibleKey = key.slice('_hidden_'.length);
        if (!(visibleKey in result)) {
          result[key] = obj[key];
        }
      }
    }
    return result;
  };

  const coerceField = (value: any, field: SchemaField): any => {
    if (value === null || value === undefined) return value;
    if (field.type === 'string' && typeof value === 'object') {
      try { return JSON.stringify(value, null, 2); } catch { return ''; }
    }
    if (field.type === 'object') return coerceObject(value, getEffectiveProperties(field, value));
    if (field.type === 'array' && Array.isArray(value)) {
      return value.map((item) => coerceField(item, field.items));
    }
    return value;
  };

  return coerceObject(data, schema.properties);
};

// ---- Restore from form (reverse of prepareSettingsForForm) ----

/**
 * Convert multiline JSON string fields back to objects after reading from SettingsForm.
 * Targets `type: 'string', multiline: true` fields that may hold serialized JSON.
 */
export const restoreSettingsFromForm = (data: any, schema: SettingsFormSchema): any => {
  const restoreObject = (obj: any, properties: Record<string, SchemaField>): any => {
    if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) return obj;
    const result: any = {};
    for (const key of Object.keys(obj)) {
      if (key in properties) {
        result[key] = restoreField(obj[key], properties[key]);
      } else {
        result[key] = obj[key];
      }
    }
    return result;
  };

  const restoreField = (value: any, field: SchemaField): any => {
    if (value === null || value === undefined) return value;
    // Multiline string → try to parse back to object
    if (field.type === 'string' && (field as any).multiline && typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try { return JSON.parse(trimmed); } catch { /* keep as string */ }
      }
      return value;
    }
    if (field.type === 'object') return restoreObject(value, getEffectiveProperties(field, value));
    if (field.type === 'array' && Array.isArray(value)) {
      return value.map((item) => restoreField(item, field.items));
    }
    return value;
  };

  return restoreObject(data, schema.properties);
};

// ---- Export ----

export const exportSettings = (settings: any) => {
  const exported = {
    _schemaVersion: SETTINGS_SCHEMA_VERSION,
    ...settings,
  };
  const blob = new Blob([JSON.stringify(exported, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `settings-v${SETTINGS_SCHEMA_VERSION}.json`;
  a.click();
  URL.revokeObjectURL(url);
};

// ---- Import ----

export interface ImportResult {
  settings: any;
  warnings: string[];
  versionWarning?: string;
}

export const importSettings = (file: File): Promise<ImportResult> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const raw = JSON.parse(e.target?.result as string);
        const { _schemaVersion, ...data } = raw;

        let versionWarning: string | undefined;
        if (_schemaVersion !== undefined && _schemaVersion !== SETTINGS_SCHEMA_VERSION) {
          if (_schemaVersion > SETTINGS_SCHEMA_VERSION) {
            versionWarning = `Ce fichier provient d'un schéma plus récent (v${_schemaVersion} vs v${SETTINGS_SCHEMA_VERSION} actuelle). Certains champs pourraient ne pas être reconnus.`;
          } else {
            versionWarning = `Ce fichier provient d'un schéma plus ancien (v${_schemaVersion} vs v${SETTINGS_SCHEMA_VERSION} actuelle). Certains champs pourraient être manquants ou obsolètes.`;
          }
        } else if (_schemaVersion === undefined) {
          versionWarning = `Ce fichier ne contient pas de version de schéma. Il pourrait ne pas être compatible avec la version actuelle (v${SETTINGS_SCHEMA_VERSION}).`;
        }

        const { sanitized, warnings } = sanitizeSettings(data, settingsSchema);
        resolve({ settings: sanitized, warnings, versionWarning });
      } catch (err) {
        reject(new Error('Fichier JSON invalide : ' + (err as any)?.message));
      }
    };
    reader.onerror = () => reject(new Error('Impossible de lire le fichier'));
    reader.readAsText(file);
  });
};
